/**
 * AIPhotoImportModal — AI 생성 사진을 갤러리에 가져오는 모달
 *
 * 탭 UI로 전체 사진/앨범별 탐색을 모두 지원.
 */

import { useCallback, useMemo, useState } from 'react'
import { ArrowLeft, Check, Image as ImageIcon } from 'lucide-react'
import { useGenerations, type Generation } from '../../../features/ai-studio/hooks/useGenerations.js'
import { useAlbums, useAlbum } from '../../../hooks/useAlbums.js'
import { useUIStore } from '../../../stores/uiStore.js'
import { Modal, Tabs, Button, Badge, Spinner, Toggle, Pagination } from '../../ui/index.js'
import styles from './SectionForm.module.css'

interface AIPhotoImportModalProps {
  open: boolean
  onClose: () => void
  existingImages: string[]
  onImport: (urls: string[]) => void
}

export function AIPhotoImportModal({ open, onClose, existingImages, onImport }: AIPhotoImportModalProps) {
  const [activeTab, setActiveTab] = useState('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const existingSet = useMemo(() => new Set(existingImages), [existingImages])

  const toggleSelect = useCallback((url: string) => {
    if (existingSet.has(url)) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  }, [existingSet])

  const addToast = useUIStore((s) => s.addToast)

  const handleImport = useCallback(() => {
    if (selected.size === 0) return
    onImport(Array.from(selected))
    addToast({ type: 'success', message: `${selected.size}장이 갤러리에 추가되었습니다` })
    setSelected(new Set())
    onClose()
  }, [selected, onImport, onClose, addToast])

  const handleClose = useCallback(() => {
    setSelected(new Set())
    onClose()
  }, [onClose])

  const tabs = [
    { key: 'all', label: '전체 사진' },
    { key: 'albums', label: '앨범별' },
  ]

  return (
    <Modal open={open} onClose={handleClose} title="AI 사진 가져오기" size="lg" headerExtra={
      selected.size > 0 ? <Badge color="blue">{selected.size}장 선택</Badge> : undefined
    }>
      <div className={styles.aiImportContent}>
        <Tabs items={tabs} value={activeTab} onChange={setActiveTab} size="sm" variant="minimal" />

        <div className={styles.aiImportBody}>
          {activeTab === 'all' ? (
            <AllPhotosTab
              selected={selected}
              existingSet={existingSet}
              onToggle={toggleSelect}
            />
          ) : (
            <AlbumsTab
              selected={selected}
              existingSet={existingSet}
              onToggle={toggleSelect}
            />
          )}
        </div>

        <div className={styles.aiImportFooter}>
          <span className={styles.aiImportCount}>
            {selected.size > 0 ? `${selected.size}장 선택됨` : '사진을 선택하세요'}
          </span>
          <Button size="sm" onClick={handleImport} disabled={selected.size === 0}>
            갤러리에 추가
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/* ── AllPhotosTab ── */

interface TabProps {
  selected: Set<string>
  existingSet: Set<string>
  onToggle: (url: string) => void
}

function AllPhotosTab({ selected, existingSet, onToggle }: TabProps) {
  const { generations, pagination, isLoading, favoritesOnly, setFavoritesOnly, page, setPage } = useGenerations()

  const allUrls = useMemo(() => {
    return generations.flatMap((g) => g.generatedUrls ?? [])
  }, [generations])

  const isInitialLoading = isLoading && allUrls.length === 0 && !favoritesOnly

  if (isInitialLoading) {
    return <div className={styles.aiImportLoading}><Spinner size="md" /></div>
  }

  return (
    <>
      <div className={styles.aiImportFilter}>
        <Toggle checked={favoritesOnly} onChange={setFavoritesOnly} size="sm" label="즐겨찾기만" />
      </div>

      {!isLoading && allUrls.length === 0 ? (
        <div className={styles.aiImportEmpty}>
          <ImageIcon size={32} strokeWidth={1.2} />
          <p>{favoritesOnly ? '즐겨찾기한 사진이 없습니다' : '생성된 AI 사진이 없습니다'}</p>
        </div>
      ) : (
        <div className={styles.aiImportGridWrap}>
          {isLoading && (
            <div className={styles.aiImportOverlay}><Spinner size="sm" /></div>
          )}
          <div className={styles.aiImportGrid}>
            {allUrls.map((url) => (
              <SelectableImage
                key={url}
                url={url}
                isSelected={selected.has(url)}
                isExisting={existingSet.has(url)}
                onToggle={onToggle}
              />
            ))}
          </div>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Pagination current={page} total={pagination.totalPages} onChange={setPage} size="sm" />
      )}
    </>
  )
}

/* ── AlbumsTab ── */

function AlbumsTab({ selected, existingSet, onToggle }: TabProps) {
  const { albums, isLoading: albumsLoading } = useAlbums()
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)

  if (albumsLoading) {
    return <div className={styles.aiImportLoading}><Spinner size="md" /></div>
  }

  if (albums.length === 0) {
    return (
      <div className={styles.aiImportEmpty}>
        <ImageIcon size={32} strokeWidth={1.2} />
        <p>앨범이 없습니다</p>
      </div>
    )
  }

  if (selectedAlbumId) {
    const albumName = albums.find((a) => a.id === selectedAlbumId)?.name
    return (
      <>
        <button
          type="button"
          className={styles.aiImportBackBtn}
          onClick={() => setSelectedAlbumId(null)}
        >
          <ArrowLeft size={16} />
          <span>{albumName ?? '목록으로'}</span>
        </button>
        <AlbumPhotos albumId={selectedAlbumId} selected={selected} existingSet={existingSet} onToggle={onToggle} />
      </>
    )
  }

  return (
    <div className={styles.aiImportAlbumGrid}>
      {albums.map((album) => {
        const previewUrl = album.images[0]?.url ?? null
        return (
          <button
            key={album.id}
            type="button"
            className={styles.aiImportAlbumCard}
            onClick={() => setSelectedAlbumId(album.id)}
          >
            <div className={styles.aiImportAlbumPreview}>
              {previewUrl
                ? <img src={previewUrl} alt="" />
                : <ImageIcon size={24} />
              }
            </div>
            <div className={styles.aiImportAlbumInfo}>
              <span className={styles.aiImportAlbumName}>{album.name}</span>
              <span className={styles.aiImportAlbumCount}>{album.images.length}장</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}

function AlbumPhotos({ albumId, selected, existingSet, onToggle }: TabProps & { albumId: string }) {
  const { album, isLoading } = useAlbum(albumId)

  if (isLoading) {
    return <div className={styles.aiImportLoading}><Spinner size="sm" /></div>
  }

  const allUrls = album?.generations?.flatMap((g) => g.generatedUrls ?? []) ?? []

  if (allUrls.length === 0) {
    return (
      <div className={styles.aiImportEmpty}>
        <p>이 앨범에 생성된 사진이 없습니다</p>
      </div>
    )
  }

  return (
    <div className={styles.aiImportGrid}>
      {allUrls.map((url) => (
        <SelectableImage
          key={url}
          url={url}
          isSelected={selected.has(url)}
          isExisting={existingSet.has(url)}
          onToggle={onToggle}
        />
      ))}
    </div>
  )
}

/* ── SelectableImage ── */

function SelectableImage({ url, isSelected, isExisting, onToggle }: {
  url: string
  isSelected: boolean
  isExisting: boolean
  onToggle: (url: string) => void
}) {
  return (
    <button
      type="button"
      className={`${styles.aiImportImageBtn} ${isSelected ? styles.aiImportImageSelected : ''} ${isExisting ? styles.aiImportImageExisting : ''}`}
      onClick={() => onToggle(url)}
      disabled={isExisting}
    >
      <img src={url} alt="" className={styles.aiImportImageThumb} />
      {isSelected && (
        <div className={styles.aiImportCheck}>
          <Check size={16} />
        </div>
      )}
      {isExisting && (
        <div className={styles.aiImportExistingBadge}>추가됨</div>
      )}
    </button>
  )
}
